'use client'

import { useMutation } from '@tanstack/react-query'

import { getTurnstileToken, TURNSTILE_ACTIONS } from '@/lib/turnstile'
import {
  confirmPasswordReset,
  googleLogin,
  loginWithEmail,
  requestPasswordReset,
  resendVerificationEmail,
  signupWithEmail,
  verifyEmail,
} from '@/service/auth.api'
import {
  GoogleLoginResponse,
  LoginResponse,
  SignupRequest,
  SignupResponse,
} from '@/types/generated/common/auth'
import {
  ConfirmPasswordResetResponse,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  VerifyEmailResponse,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const useLoginWithEmail = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: LoginResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: loginWithEmail,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useSignupWithEmail = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: SignupResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload: SignupRequest) =>
      signupWithEmail(payload, await getTurnstileToken(TURNSTILE_ACTIONS.signup)),
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useVerifyEmail = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: VerifyEmailResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: verifyEmail,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useResendVerificationEmail = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: ResendVerificationResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload: ResendVerificationRequest) =>
      resendVerificationEmail(
        payload,
        await getTurnstileToken(TURNSTILE_ACTIONS.resendVerification)
      ),
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useGoogleLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: GoogleLoginResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: googleLogin,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useRequestPasswordReset = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: RequestPasswordResetResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload: RequestPasswordResetRequest) =>
      requestPasswordReset(payload, await getTurnstileToken(TURNSTILE_ACTIONS.passwordReset)),
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useConfirmPasswordReset = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: ConfirmPasswordResetResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirmPasswordReset,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}
